import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { BookUser, Plus, ShieldQuestion } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const EMPTY = { name: '', abn: '', related_group_id: '', credit_limit: '', limit_source: 'APPROVED', supporting_evidence: '', approved_date: '' };
const today = () => new Date().toISOString().slice(0, 10);

export const BuyerLedgerPanel = ({ api, policy }) => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // {buyer_id, type: 'withdraw'|'overdue'|'claim', fields}
  const [result, setResult] = useState(null);

  const load = useCallback(() => api.get(`/trade-credit/${policy.policy_id}/buyers`)
    .then(r => setData(r.data)).catch(() => {}), [api, policy.policy_id]);
  useEffect(() => { load(); }, [load]);

  const addBuyer = async () => {
    if (!form.name || !Number(form.credit_limit)) { toast.error('Buyer name and credit limit are required'); return; }
    setBusy(true);
    try {
      await api.post(`/trade-credit/${policy.policy_id}/buyers`, {
        ...form, credit_limit: Number(form.credit_limit),
        related_group_id: form.related_group_id || null, approved_date: form.approved_date || null,
      });
      toast.success(`Credit limit approved for ${form.name}`);
      setForm(EMPTY); setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const run = async () => {
    if (!action) return;
    try {
      if (action.type === 'withdraw') {
        await api.post(`/trade-credit/buyers/${action.buyer_id}/withdraw`,
          { effective_date: action.effective_date || today(), reason: action.reason || '' });
        toast.success('Limit withdrawn — prospective only, deliveries before the notice remain covered');
      } else if (action.type === 'overdue') {
        const r = await api.post(`/trade-credit/${policy.policy_id}/buyers/${action.buyer_id}/overdue`,
          { overdue_since: action.overdue_since || today(), amount: Number(action.amount) || 0 });
        toast.success(`Overdue clock started — notification due ${r.data.overdue.notification_due_date}`);
      } else if (action.type === 'claim') {
        const r = await api.post(`/trade-credit/${policy.policy_id}/claim-eligibility`,
          { buyer_id: action.buyer_id, delivery_date: action.delivery_date || today(), debt_amount: Number(action.debt_amount) || 0 });
        setResult(r.data);
      }
      setAction(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const ainp = (field, ph, extra = {}) => (
    <input value={action?.[field] || ''} onChange={e => setAction(p => ({ ...p, [field]: e.target.value }))}
      placeholder={ph} data-testid={`tc-action-${field}-input`}
      className="px-2 py-1 border border-input rounded-sm text-[11px] bg-white" {...extra} />
  );

  return (
    <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(26,76,124,0.25)' }} data-testid="buyer-ledger-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2">
          <BookUser className="w-4 h-4 mt-0.5" style={{ color: C.blue }} />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Buyer Sub-Ledger — Credit Limits</h3>
            <p className="text-[11px]" style={{ color: C.slate }}>The buyer, not the policy, is the unit of underwriting. Withdrawals are prospective only (TC-E-006); the overdue clock is a condition precedent (TC-E-002).</p>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="tc-add-buyer-toggle-btn"
          className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium text-white" style={{ background: C.blue }}>
          <Plus className="w-3.5 h-3.5" /> Approve Buyer Limit
        </button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-4 text-[11px]" data-testid="buyer-ledger-totals" style={{ color: C.slate }}>
          <span>Buyers <strong style={{ color: C.navy }}>{data.totals.count}</strong></span>
          <span>Active <strong style={{ color: C.green }}>{data.totals.active}</strong></span>
          <span>Withdrawn <strong style={{ color: C.red }}>{data.totals.withdrawn}</strong></span>
          <span>Overdue <strong style={{ color: C.amber }}>{data.totals.overdue}</strong></span>
          <span>Aggregate limits <strong style={{ color: C.navy }}>${data.totals.aggregate_limits.toLocaleString()}</strong></span>
        </div>
      )}

      {showForm && (
        <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(26,76,124,0.05)' }} data-testid="tc-buyer-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Buyer name" data-testid="tc-buyer-name-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
            <input value={form.abn} onChange={e => setForm(p => ({ ...p, abn: e.target.value }))} placeholder="ABN" data-testid="tc-buyer-abn-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
            <input value={form.credit_limit} onChange={e => setForm(p => ({ ...p, credit_limit: e.target.value }))} placeholder="Credit limit $" type="number" data-testid="tc-buyer-limit-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
            <select value={form.limit_source} onChange={e => setForm(p => ({ ...p, limit_source: e.target.value }))} data-testid="tc-buyer-source-select" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white">
              <option value="APPROVED">Approved limit</option><option value="DISCRETIONARY">Discretionary (TC-E-001)</option>
            </select>
            <input value={form.related_group_id} onChange={e => setForm(p => ({ ...p, related_group_id: e.target.value }))} placeholder="Related group (TC-E-043, optional)" data-testid="tc-buyer-group-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
            <input value={form.supporting_evidence} onChange={e => setForm(p => ({ ...p, supporting_evidence: e.target.value }))} placeholder="Evidence — report / refs / financials" data-testid="tc-buyer-evidence-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white md:col-span-2" />
            <input value={form.approved_date} onChange={e => setForm(p => ({ ...p, approved_date: e.target.value }))} type="date" data-testid="tc-buyer-approved-date-input" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
          </div>
          <button onClick={addBuyer} disabled={busy} data-testid="tc-buyer-submit-btn"
            className="px-4 py-1.5 rounded-sm text-xs font-medium text-white disabled:opacity-40" style={{ background: C.navy }}>
            {busy ? 'Saving…' : 'Approve Limit'}
          </button>
        </div>
      )}

      {data && data.buyers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left" style={{ color: C.slate }}>
              <th className="py-1.5 pr-2 font-medium">Buyer</th><th className="py-1.5 pr-2 font-medium">Limit</th>
              <th className="py-1.5 pr-2 font-medium">Source</th><th className="py-1.5 pr-2 font-medium">Status</th>
              <th className="py-1.5 pr-2 font-medium">Overdue clock</th><th className="py-1.5 font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {data.buyers.map(b => (
                <tr key={b.buyer_id} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`tc-buyer-row-${b.buyer_id}`}>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-[10px]" style={{ color: C.slate }}>{b.abn}{b.related_group_id ? ` · group ${b.related_group_id}` : ''} · approved {b.approved_date}</div>
                  </td>
                  <td className="py-1.5 pr-2 font-mono">${(b.credit_limit || 0).toLocaleString()}</td>
                  <td className="py-1.5 pr-2 text-[10px]">{b.limit_source}</td>
                  <td className="py-1.5 pr-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      title={b.withdrawal ? `Withdrawn effective ${b.withdrawal.effective_date} — ${b.withdrawal.prospective_note}` : ''}
                      style={{ background: b.status === 'active' ? 'rgba(29,158,117,0.1)' : 'rgba(226,75,74,0.12)', color: b.status === 'active' ? C.green : C.red }}>
                      {b.status}{b.withdrawal ? ` from ${b.withdrawal.effective_date}` : ''}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: b.overdue?.notified_late ? C.red : C.slate }}>
                    {b.overdue ? `${b.overdue.days_overdue}d overdue · notify by ${b.overdue.notification_due_date}${b.overdue.notified_late ? ' · LATE — claim blocked' : ''}` : '—'}
                  </td>
                  <td className="py-1.5">
                    <span className="flex flex-wrap gap-1.5">
                      {b.status === 'active' && (
                        <button onClick={() => { setResult(null); setAction({ buyer_id: b.buyer_id, type: 'withdraw', effective_date: today() }); }} data-testid={`tc-withdraw-btn-${b.buyer_id}`}
                          className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>Withdraw</button>
                      )}
                      <button onClick={() => { setResult(null); setAction({ buyer_id: b.buyer_id, type: 'overdue', overdue_since: today() }); }} data-testid={`tc-overdue-btn-${b.buyer_id}`}
                        className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(232,160,32,0.12)', color: '#A8720E' }}>Overdue</button>
                      <button onClick={() => { setResult(null); setAction({ buyer_id: b.buyer_id, type: 'claim', delivery_date: today() }); }} data-testid={`tc-eligibility-btn-${b.buyer_id}`}
                        className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>Check Debt</button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.buyers.length === 0 && (
        <p className="text-xs py-2 text-center" style={{ color: C.slate }} data-testid="buyer-ledger-empty">
          No buyer credit limits approved under this policy yet.
        </p>
      )}

      {action && (
        <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(13,27,42,0.04)' }} data-testid="tc-action-form">
          <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            {action.type === 'withdraw' ? 'Withdraw credit limit — prospective only (applies to the delivery date, never the claim date)'
              : action.type === 'overdue' ? 'Report account overdue — starts the Maximum Extension Period clock'
                : 'Claim eligibility — test a debt against the ledger'}
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            {action.type === 'withdraw' && (<>
              {ainp('effective_date', '', { type: 'date', min: today() })}
              {ainp('reason', 'Reason (min 10 chars)', { style: { width: '260px' } })}
            </>)}
            {action.type === 'overdue' && (<>
              {ainp('overdue_since', '', { type: 'date' })}
              {ainp('amount', 'Amount overdue $', { type: 'number' })}
            </>)}
            {action.type === 'claim' && (<>
              {ainp('delivery_date', '', { type: 'date' })}
              {ainp('debt_amount', 'Debt amount $', { type: 'number' })}
            </>)}
            <button onClick={run} data-testid="tc-action-submit-btn"
              className="px-3 py-1 rounded-sm text-[11px] font-medium text-white" style={{ background: C.navy }}>Run</button>
            <button onClick={() => setAction(null)} className="text-[10px] underline" style={{ color: C.slate }}>cancel</button>
          </div>
        </div>
      )}

      {result && (
        <div className="p-3 rounded-sm space-y-1" data-testid="tc-eligibility-result"
          style={{ background: result.covered ? 'rgba(29,158,117,0.07)' : 'rgba(226,75,74,0.07)', border: `1px solid ${result.covered ? 'rgba(29,158,117,0.3)' : 'rgba(226,75,74,0.3)'}` }}>
          <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: result.covered ? C.green : C.red }}>
            <ShieldQuestion className="w-3.5 h-3.5" />
            {result.covered ? `COVERED — estimated payable $${(result.payable_estimate || 0).toLocaleString()} (${result.indemnity_percentage}% indemnity)` : 'NOT COVERED'}
          </p>
          {(result.reasons || []).map((r, i) => <p key={i} className="text-[11px]" style={{ color: C.red }}>• {r}</p>)}
          {(result.notes || []).map((n, i) => <p key={i} className="text-[11px]" style={{ color: C.slate }}>• {n}</p>)}
        </div>
      )}
    </Card>
  );
};

export default BuyerLedgerPanel;
